import React, { useMemo } from "react";
import type { CategoryProgress, PublicHeldBadge } from "../types";
import { BadgeTile, ProgressGhost, type BadgeTileSize } from "./BadgeTile";
import { categoryRank, compareShelf, familyAccent, familyLabel, familyRank } from "./badgeFormat";
import "./badgeCollection.css";

type ShelfItem =
  | { kind: "held"; key: string; badge: PublicHeldBadge }
  | { kind: "ghost"; key: string; progress: CategoryProgress };

export interface Shelf {
  family: string;
  held: number;
  items: ShelfItem[];
}

/**
 * Group held badges (and, for the member's own collection, the next tier of each
 * category as a ghost) into shelves by family. Inside a shelf a category's tiers
 * read left to right, and its ghost follows them.
 */
export function buildShelves(badges: PublicHeldBadge[], progress: CategoryProgress[] = []): Shelf[] {
  const byFamily = new Map<string, { held: PublicHeldBadge[]; ghosts: CategoryProgress[] }>();
  const slot = (family: string) => {
    const key = family || "other";
    if (!byFamily.has(key)) byFamily.set(key, { held: [], ghosts: [] });
    return byFamily.get(key)!;
  };
  badges.forEach((b) => slot(b.family).held.push(b));
  progress.filter((p) => p.next).forEach((p) => slot(p.family || p.next!.family).ghosts.push(p));

  const shelves: Shelf[] = [];
  byFamily.forEach(({ held, ghosts }, family) => {
    const sorted = [...held].sort(compareShelf);
    const items: ShelfItem[] = [];
    const categories = Array.from(new Set([...sorted.map((b) => b.category), ...ghosts.map((g) => g.category)])).sort(
      (a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b)
    );
    for (const category of categories) {
      sorted
        .filter((b) => b.category === category)
        .forEach((b) => items.push({ kind: "held", key: `held:${b.code}`, badge: b }));
      ghosts
        .filter((g) => g.category === category)
        .forEach((g) => items.push({ kind: "ghost", key: `ghost:${g.next!.code}`, progress: g }));
    }
    shelves.push({ family, held: sorted.length, items });
  });
  return shelves.sort((a, b) => familyRank(a.family) - familyRank(b.family) || a.family.localeCompare(b.family));
}

const BadgeShelves: React.FC<{
  badges: PublicHeldBadge[];
  progress?: CategoryProgress[];
  pinned?: readonly string[];
  size?: BadgeTileSize;
  onOpen: (code: string) => void;
}> = ({ badges, progress, pinned = [], size = "tile", onOpen }) => {
  const shelves = useMemo(() => buildShelves(badges, progress), [badges, progress]);
  if (!shelves.length) return null;
  return (
    <div className="bcol-shelves">
      {shelves.map((shelf) => (
        <section
          key={shelf.family}
          className="bcol-shelf"
          style={{ "--bcol-accent": familyAccent(shelf.family) } as React.CSSProperties}
          aria-labelledby={`bcol-shelf-${shelf.family}`}
        >
          <header className="bcol-shelf__head">
            <h2 id={`bcol-shelf-${shelf.family}`} className="bcol-shelf__title">
              {familyLabel(shelf.family)}
            </h2>
            <span className="bcol-shelf__count">
              {shelf.held} {shelf.held === 1 ? "badge" : "badges"}
            </span>
          </header>
          <div className={`bcol-shelf__grid bcol-shelf__grid--${size}`}>
            {shelf.items.map((item) =>
              item.kind === "held" ? (
                <BadgeTile
                  key={item.key}
                  badge={item.badge}
                  size={size}
                  pinned={pinned.includes(item.badge.code)}
                  onOpen={onOpen}
                />
              ) : (
                <ProgressGhost key={item.key} progress={item.progress} size={size} onOpen={onOpen} />
              )
            )}
          </div>
          <div className="bcol-shelf__plank" aria-hidden="true" />
        </section>
      ))}
    </div>
  );
};

export default BadgeShelves;
